import type { StructureT } from "@/app/types/structure";
import type { VehicleT } from "@/app/types/gebrauchtwagen";

import {
  getVehicleCondition,
  getVehicleFuelId,
  getVehicleLocation,
  getVehicleMakeId,
  getVehicleModelId,
  getVehicleTransmissionId,
  getVehicleTypeGroup,
  normalizeSearchTerm,
  resolveVehicleColorValue,
  vehicleMatchesSearch,
} from "./runtime-utils";
import { isExcludedMakeLabel } from "./constants";
import type {
  VehicleColorFilterValue,
  VehicleConditionFilterValue,
  VehicleFuelFilterValue,
  VehicleKilometersFilterValue,
  VehicleLocationFilterValue,
  VehicleMakeFilterValue,
  VehicleModelFilterValue,
  VehiclePowerRangeFilterValue,
  VehiclePriceRangeFilterValue,
  VehicleTransmissionFilterValue,
  VehicleTypeFilterValue,
} from "./value-types";
import type { FilterControl, SelectFilterOption } from "./types";

export type FilterOption<Value extends string> = {
  value: Value;
  label: string;
  count?: number;
  disabled?: boolean;
};

export type FilterOptionContext = {
  structure: StructureT;
  values: VehicleFilterValues;
  facets?: VehicleFilterFacets;
};

export type FilterFacetContext = {
  structure: StructureT;
  values: VehicleFilterValues;
};

export type FilterMatchArgs<Value> = {
  value: Value;
  values: VehicleFilterValues;
  vehicle: VehicleT;
  structure: StructureT;
};

export type FilterFacetConfig = {
  getKeys: (vehicle: VehicleT, ctx: FilterFacetContext) => string[];
};

type FilterDefinitionBase<
  Id extends string,
  Value,
  ApiValue,
  Control extends FilterControl,
> = {
  id: Id;
  label: string;
  param: string;
  apiKey: string;
  control: Control;
  defaultValue: Value;
  parse: (params: URLSearchParams) => Value;
  serialize: (params: URLSearchParams, value: Value) => void;
  validateQuery: (raw: string | null) => boolean;
  isActive: (value: Value) => boolean;
  matches: (args: FilterMatchArgs<Value>) => boolean;
  toApi: (value: Value, values: VehicleFilterValues) => ApiValue | undefined;
  formatChip?: (value: Value, ctx: { structure?: StructureT }) => string | null;
  getOptions?: (
    context: FilterOptionContext
  ) => ReadonlyArray<FilterOption<string>>;
  facet?: FilterFacetConfig;
};

export type VehicleFilterDefinition =
  | FilterDefinitionBase<
      "search",
      string,
      string,
      Extract<FilterControl, { kind: "search" }>
    >
  | FilterDefinitionBase<
      "condition",
      VehicleConditionFilterValue | null,
      VehicleConditionFilterValue,
      Extract<FilterControl, { kind: "checkbox" }>
    >
  | FilterDefinitionBase<
      "make",
      VehicleMakeFilterValue | null,
      VehicleMakeFilterValue,
      Extract<FilterControl, { kind: "select" }>
    >
  | FilterDefinitionBase<
      "model",
      VehicleModelFilterValue | null,
      VehicleModelFilterValue,
      Extract<FilterControl, { kind: "select" }>
    >
  | FilterDefinitionBase<
      "vehicletype",
      VehicleTypeFilterValue[],
      VehicleTypeFilterValue[],
      Extract<FilterControl, { kind: "checkbox" }>
    >
  | FilterDefinitionBase<
      "color",
      VehicleColorFilterValue[],
      VehicleColorFilterValue[],
      Extract<FilterControl, { kind: "checkbox" }>
    >
  | FilterDefinitionBase<
      "fuel",
      VehicleFuelFilterValue[],
      VehicleFuelFilterValue[],
      Extract<FilterControl, { kind: "checkbox" }>
    >
  | FilterDefinitionBase<
      "transmission",
      VehicleTransmissionFilterValue[],
      VehicleTransmissionFilterValue[],
      Extract<FilterControl, { kind: "checkbox" }>
    >
  | FilterDefinitionBase<
      "location",
      VehicleLocationFilterValue[],
      VehicleLocationFilterValue[],
      Extract<FilterControl, { kind: "checkbox" }>
    >
  | FilterDefinitionBase<
      "kilometers",
      VehicleKilometersFilterValue,
      VehicleKilometersFilterValue,
      Extract<FilterControl, { kind: "range" }>
    >
  | FilterDefinitionBase<
      "priceRange",
      VehiclePriceRangeFilterValue,
      VehiclePriceRangeFilterValue,
      Extract<FilterControl, { kind: "range" }>
    >
  | FilterDefinitionBase<
      "powerRange",
      VehiclePowerRangeFilterValue,
      VehiclePowerRangeFilterValue,
      Extract<FilterControl, { kind: "range" }>
    >;

type DefinitionMap = {
  search: Extract<VehicleFilterDefinition, { id: "search" }>;
  condition: Extract<VehicleFilterDefinition, { id: "condition" }>;
  make: Extract<VehicleFilterDefinition, { id: "make" }>;
  model: Extract<VehicleFilterDefinition, { id: "model" }>;
  vehicletype: Extract<VehicleFilterDefinition, { id: "vehicletype" }>;
  color: Extract<VehicleFilterDefinition, { id: "color" }>;
  fuel: Extract<VehicleFilterDefinition, { id: "fuel" }>;
  transmission: Extract<VehicleFilterDefinition, { id: "transmission" }>;
  location: Extract<VehicleFilterDefinition, { id: "location" }>;
  kilometers: Extract<VehicleFilterDefinition, { id: "kilometers" }>;
  priceRange: Extract<VehicleFilterDefinition, { id: "priceRange" }>;
  powerRange: Extract<VehicleFilterDefinition, { id: "powerRange" }>;
};

export const conditionOptions = [
  { value: "new", label: "Neuwagen" },
  { value: "used", label: "Gebrauchtwagen" },
] as const satisfies ReadonlyArray<FilterOption<VehicleConditionFilterValue>>;

export const vehicleTypeOptions = [
  { value: "small", label: "Kleinwagen" },
  { value: "station-wagon", label: "Kombi" },
  { value: "sedan", label: "Limousine" },
  { value: "sports-coupe", label: "Sportwagen / Coupé" },
  { value: "convertible", label: "Cabrio" },
  { value: "suv-pickup", label: "Geländewagen / SUV / Pickup" },
  { value: "commercial", label: "Nutzfahrzeuge" },
  { value: "camper", label: "Camper" },
] as const satisfies ReadonlyArray<FilterOption<VehicleTypeFilterValue>>;

export const colorOptions = [
  { value: "red", label: "rot" },
  { value: "gray", label: "grau" },
  { value: "black", label: "schwarz" },
  { value: "white", label: "weiß" },
  { value: "silver", label: "silber" },
  { value: "yellow", label: "gelb" },
  { value: "blue", label: "blau" },
  { value: "brown", label: "braun" },
  { value: "beige", label: "beige" },
  { value: "green", label: "grün" },
] as const satisfies ReadonlyArray<FilterOption<VehicleColorFilterValue>>;

export const locationOptions = [
  { value: "Darmstadt", label: "Darmstadt" },
  { value: "Bensheim", label: "Bensheim" },
  { value: "Lampertheim", label: "Lampertheim" },
  { value: "Groß-Umstadt", label: "Groß-Umstadt" },
] as const satisfies ReadonlyArray<FilterOption<VehicleLocationFilterValue>>;

function ensureOptionCount<Value extends string>(
  option: FilterOption<Value>,
  counts?: Record<string, number>
): FilterOption<Value> {
  if (!counts) {
    return option;
  }

  return {
    ...option,
    count: counts[option.value] ?? 0,
  };
}

const VEHICLE_FILTER_DEFINITIONS = [
  {
    id: "search",
    label: "Suche",
    param: "q",
    apiKey: "search",
    control: {
      kind: "search",
      placeholder: "Fahrzeug suchen...",
    } as DefinitionMap["search"]["control"],
    defaultValue: "",
    parse: (params: URLSearchParams) => {
      const raw = params.get("q");
      return raw ? raw.trim() : "";
    },
    serialize: (params: URLSearchParams, value: string) => {
      const normalized = value.trim();
      if (normalized.length > 0) {
        params.set("q", normalized);
      } else {
        params.delete("q");
      }
    },
    validateQuery: () => true,
    isActive: (value: string) => value.trim().length > 0,
    matches: ({ value, vehicle, structure }) => {
      const searchTerm = normalizeSearchTerm(value);
      if (!searchTerm) {
        return true;
      }
      return vehicleMatchesSearch(vehicle, structure, searchTerm);
    },
    toApi: (value: string) => {
      const normalized = value.trim();
      return normalized.length > 0 ? normalized : undefined;
    },
    formatChip: (value: string) => {
      const normalized = value.trim();
      return normalized.length > 0 ? `Suche: "${normalized}"` : null;
    },
  } satisfies DefinitionMap["search"],
  {
    id: "condition",
    label: "Zustand",
    param: "condition",
    apiKey: "condition",
    control: {
      kind: "checkbox",
      multiple: false,
      options: conditionOptions,
    } as DefinitionMap["condition"]["control"],
    defaultValue: null,
    parse: (params: URLSearchParams) => {
      const raw = params.get("condition");
      return raw === "new" || raw === "used" ? raw : null;
    },
    serialize: (
      params: URLSearchParams,
      value: VehicleConditionFilterValue | null
    ) => {
      if (value) {
        params.set("condition", value);
      } else {
        params.delete("condition");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      return raw === "new" || raw === "used";
    },
    isActive: (value: VehicleConditionFilterValue | null) => value !== null,
    matches: ({ value, vehicle }) => {
      if (!value) return true;
      return getVehicleCondition(vehicle) === value;
    },
    toApi: (value: VehicleConditionFilterValue | null) => value ?? undefined,
    formatChip: (value) => {
      if (!value) return null;
      const option = conditionOptions.find((opt) => opt.value === value);
      return option ? option.label : value;
    },
    getOptions: ({ facets }) => {
      const counts = facets?.counts?.condition ?? {};
      return conditionOptions
        .map((option) => ensureOptionCount(option, counts))
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle) => {
        const condition = getVehicleCondition(vehicle);
        return condition ? [condition] : [];
      },
    },
  } satisfies DefinitionMap["condition"],
  {
    id: "make",
    label: "Marke",
    param: "make",
    apiKey: "make",
    control: {
      kind: "select",
      placeholder: "Marke auswählen",
      allowClear: true,
    } as DefinitionMap["make"]["control"],
    defaultValue: null,
    parse: (params: URLSearchParams) => {
      const raw = params.get("make");
      return raw && raw.trim().length > 0 ? raw.trim() : null;
    },
    serialize: (
      params: URLSearchParams,
      value: VehicleMakeFilterValue | null
    ) => {
      if (value) {
        params.set("make", value);
      } else {
        params.delete("make");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      return raw.trim().length > 0;
    },
    isActive: (value: VehicleMakeFilterValue | null) => Boolean(value),
    matches: ({ value, vehicle, structure }) => {
      if (!value) return true;
      const makeId = getVehicleMakeId(vehicle);
      if (!makeId) {
        return false;
      }
      const label = structure.make?.[makeId];
      if (isExcludedMakeLabel(label)) {
        return false;
      }
      return makeId === value;
    },
    toApi: (value: VehicleMakeFilterValue | null) => value ?? undefined,
    formatChip: (value, ctx) => {
      if (!value) return null;
      const label = ctx.structure?.make?.[value];
      return label ?? value;
    },
    getOptions: ({ facets, structure, values }) => {
      const counts = facets?.counts?.make ?? {};
      const structureEntries = Object.entries(structure.make ?? {}).filter(
        ([, label]) => !isExcludedMakeLabel(label)
      );

      const entries = structureEntries.map(([value, label]) => ({
        value,
        label: label ?? value,
        count: counts[value] ?? 0,
      }));

      const selectedMake = values.make;
      if (
        selectedMake &&
        !entries.some((option) => option.value === selectedMake)
      ) {
        const label = structure.make?.[selectedMake] ?? selectedMake;
        if (!isExcludedMakeLabel(label)) {
          entries.push({
            value: selectedMake,
            label,
            count: counts[selectedMake] ?? 0,
          });
        }
      }

      const hasPositiveCounts = entries.some((option) => option.count > 0);

      return entries
        .filter((option) =>
          hasPositiveCounts
            ? option.count > 0 || option.value === values.make
            : true
        )
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle, ctx) => {
        const make = getVehicleMakeId(vehicle);
        if (!make) {
          return [];
        }
        const label = ctx.structure.make?.[make];
        if (isExcludedMakeLabel(label)) {
          return [];
        }
        return [make];
      },
    },
  } satisfies DefinitionMap["make"],
  {
    id: "model",
    label: "Modell",
    param: "model",
    apiKey: "model",
    control: {
      kind: "select",
      placeholder: "Modell auswählen",
      allowClear: true,
    } as DefinitionMap["model"]["control"],
    defaultValue: null,
    parse: (params: URLSearchParams) => {
      const raw = params.get("model");
      return raw && raw.trim().length > 0 ? raw.trim() : null;
    },
    serialize: (
      params: URLSearchParams,
      value: VehicleModelFilterValue | null
    ) => {
      if (value) {
        params.set("model", value);
      } else {
        params.delete("model");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      return raw.trim().length > 0;
    },
    isActive: (value: VehicleModelFilterValue | null) => Boolean(value),
    matches: ({ value, vehicle }) => {
      if (!value) return true;
      return getVehicleModelId(vehicle) === value;
    },
    toApi: (value: VehicleModelFilterValue | null) => value ?? undefined,
    formatChip: (value, ctx) => {
      if (!value) return null;
      const label = ctx.structure?.model?.[value];
      return label ?? value;
    },
    getOptions: ({ facets, structure, values }) => {
      const selectedMake = values.make;
      if (!selectedMake) {
        return [];
      }
      const makeLabel = structure.make?.[selectedMake];
      if (isExcludedMakeLabel(makeLabel)) {
        return [];
      }
      const counts = facets?.counts?.model ?? {};

      const structureModels = Object.entries(structure.model ?? {})
        .filter(
          ([modelId]) => counts[`${selectedMake}|${modelId}`] !== undefined
        )
        .map(
          ([modelId]) =>
            ({
              value: modelId,
              label: structure.model?.[modelId] ?? modelId,
              count: counts[`${selectedMake}|${modelId}`] ?? 0,
            }) satisfies FilterOption<string>
        );

      const hasPositiveCounts = structureModels.some(
        (option) => option.count > 0
      );

      const selectedModel = values.model;
      if (
        selectedModel &&
        !structureModels.some((option) => option.value === selectedModel)
      ) {
        structureModels.push({
          value: selectedModel,
          label: structure.model?.[selectedModel] ?? selectedModel,
          count: counts[`${selectedMake}|${selectedModel}`] ?? 0,
        });
      }

      return structureModels
        .filter((option) =>
          hasPositiveCounts
            ? option.count > 0 || option.value === values.model
            : true
        )
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle, ctx) => {
        const make = getVehicleMakeId(vehicle);
        const model = getVehicleModelId(vehicle);
        if (!make || !model) {
          return [];
        }
        const label = ctx.structure.make?.[make];
        if (isExcludedMakeLabel(label)) {
          return [];
        }
        return [`${make}|${model}`];
      },
    },
  } satisfies DefinitionMap["model"],
  {
    id: "priceRange",
    label: "Preis",
    param: "price",
    apiKey: "priceRange",
    control: {
      kind: "range",
      min: 0,
      max: 200000,
      step: 5000,
      unit: "€",
    },
    defaultValue: { min: null, max: null },

    parse: (params: URLSearchParams) => {
      const minStr = params.get("price_min");
      const maxStr = params.get("price_max");
      return {
        min: minStr ? parseInt(minStr, 10) : null,
        max: maxStr ? parseInt(maxStr, 10) : null,
      };
    },

    serialize: (
      params: URLSearchParams,
      value: VehiclePriceRangeFilterValue
    ) => {
      const CONTROL_MIN = 0;
      const CONTROL_MAX = 200000;

      if (value.min !== null && value.min !== CONTROL_MIN) {
        params.set("price_min", String(value.min));
      } else {
        params.delete("price_min");
      }
      if (value.max !== null && value.max !== CONTROL_MAX) {
        params.set("price_max", String(value.max));
      } else {
        params.delete("price_max");
      }
    },

    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      const num = parseInt(raw, 10);
      return !isNaN(num) && num >= 0;
    },

    isActive: (value: VehiclePriceRangeFilterValue) =>
      value.min !== null || value.max !== null,

    matches: ({
      value,
      vehicle,
    }: {
      value: VehiclePriceRangeFilterValue;
      vehicle: VehicleT;
    }) => {
      if (value.min === null && value.max === null) return true;
      const price = vehicle.customerprice;
      if (value.min !== null && price < value.min) return false;
      if (value.max !== null && price > value.max) return false;
      return true;
    },

    toApi: (value: VehiclePriceRangeFilterValue) => {
      if (value.min === null && value.max === null) return undefined;
      return value;
    },

    formatChip: (value: VehiclePriceRangeFilterValue) => {
      if (value.min === null && value.max === null) return null;
      if (value.min !== null && value.max !== null) {
        return `${value.min.toLocaleString("de")} - ${value.max.toLocaleString("de")} €`;
      }
      if (value.min !== null) {
        return `Ab ${value.min.toLocaleString("de")} €`;
      }
      return `Bis ${value.max?.toLocaleString("de")} €`;
    },
  } satisfies DefinitionMap["priceRange"],
  {
    id: "powerRange",
    label: "Leistung",
    param: "power",
    apiKey: "powerRange",
    control: {
      kind: "range",
      min: 0,
      max: 588, // 588 kW ≈ 800 PS (588 * 1.36 = 800 PS)
      step: 7, // 7 kW Schritte (≈ 10 PS)
      unit: "PS", // UI zeigt PS an
    },
    defaultValue: { min: null, max: null },

    parse: (params: URLSearchParams) => {
      const minStr = params.get("power_min");
      const maxStr = params.get("power_max");
      return {
        min: minStr ? parseInt(minStr, 10) : null,
        max: maxStr ? parseInt(maxStr, 10) : null,
      };
    },

    serialize: (
      params: URLSearchParams,
      value: VehiclePowerRangeFilterValue
    ) => {
      const CONTROL_MIN = 0;
      const CONTROL_MAX = 588;

      if (value.min !== null && value.min !== CONTROL_MIN) {
        params.set("power_min", String(value.min));
      } else {
        params.delete("power_min");
      }
      if (value.max !== null && value.max !== CONTROL_MAX) {
        params.set("power_max", String(value.max));
      } else {
        params.delete("power_max");
      }
    },

    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      const num = parseInt(raw, 10);
      return !isNaN(num) && num >= 0;
    },

    isActive: (value: VehiclePowerRangeFilterValue) =>
      value.min !== null || value.max !== null,

    matches: ({
      value,
      vehicle,
    }: {
      value: VehiclePowerRangeFilterValue;
      vehicle: VehicleT;
    }) => {
      if (value.min === null && value.max === null) return true;
      const powerInKw = vehicle.power; // API liefert KW
      if (value.min !== null && powerInKw < value.min) return false;
      if (value.max !== null && powerInKw > value.max) return false;
      return true;
    },

    toApi: (value: VehiclePowerRangeFilterValue) => {
      if (value.min === null && value.max === null) return undefined;
      return value;
    },

    formatChip: (value: VehiclePowerRangeFilterValue) => {
      if (value.min === null && value.max === null) return null;

      // Konvertiere KW zu PS für die Anzeige (1 kW ≈ 1.36 PS)
      const convertKwToPs = (kw: number) => Math.round(kw * 1.36);

      if (value.min !== null && value.max !== null) {
        const minPs = convertKwToPs(value.min);
        const maxPs = convertKwToPs(value.max);
        return `${minPs.toLocaleString("de")} - ${maxPs.toLocaleString("de")} PS`;
      }
      if (value.min !== null) {
        const minPs = convertKwToPs(value.min);
        return `Ab ${minPs.toLocaleString("de")} PS`;
      }
      if (value.max !== null) {
        const maxPs = convertKwToPs(value.max);
        return `Bis ${maxPs.toLocaleString("de")} PS`;
      }
      return null;
    },
  } satisfies DefinitionMap["powerRange"],
  {
    id: "kilometers",
    label: "Kilometerstand",
    param: "km",
    apiKey: "kilometers",
    control: {
      kind: "range",
      min: 0,
      max: 150000,
      step: 5000,
      unit: "km",
    },
    defaultValue: { min: null, max: null },

    parse: (params: URLSearchParams) => {
      const minStr = params.get("km_min");
      const maxStr = params.get("km_max");
      return {
        min: minStr ? parseInt(minStr, 10) : null,
        max: maxStr ? parseInt(maxStr, 10) : null,
      };
    },

    serialize: (
      params: URLSearchParams,
      value: VehicleKilometersFilterValue
    ) => {
      const CONTROL_MIN = 0;
      const CONTROL_MAX = 150000;

      if (value.min !== null && value.min !== CONTROL_MIN) {
        params.set("km_min", String(value.min));
      } else {
        params.delete("km_min");
      }
      if (value.max !== null && value.max !== CONTROL_MAX) {
        params.set("km_max", String(value.max));
      } else {
        params.delete("km_max");
      }
    },

    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      const num = parseInt(raw, 10);
      return !isNaN(num) && num >= 0;
    },

    isActive: (value: VehicleKilometersFilterValue) =>
      value.min !== null || value.max !== null,

    matches: ({
      value,
      vehicle,
    }: {
      value: VehicleKilometersFilterValue;
      vehicle: VehicleT;
    }) => {
      if (value.min === null && value.max === null) return true;
      const km = vehicle.kilometers;
      if (value.min !== null && km < value.min) return false;
      if (value.max !== null && km > value.max) return false;
      return true;
    },

    toApi: (value: VehicleKilometersFilterValue) => {
      if (value.min === null && value.max === null) return undefined;
      return value;
    },

    formatChip: (value: VehicleKilometersFilterValue) => {
      if (value.min === null && value.max === null) return null;
      if (value.min !== null && value.max !== null) {
        return `${value.min.toLocaleString("de")} - ${value.max.toLocaleString("de")} km`;
      }
      if (value.min !== null) {
        return `Ab ${value.min.toLocaleString("de")} km`;
      }
      return `Bis ${value.max?.toLocaleString("de")} km`;
    },
  } satisfies DefinitionMap["kilometers"],
  {
    id: "vehicletype",
    label: "Fahrzeugtyp",
    param: "vehicletype",
    apiKey: "vehicletype",
    control: {
      kind: "checkbox",
      multiple: true,
      options: vehicleTypeOptions,
    } as DefinitionMap["vehicletype"]["control"],
    defaultValue: [],
    parse: (params: URLSearchParams) => {
      const raw = params.get("vehicletype");
      if (!raw) return [];
      const allowed = new Set(vehicleTypeOptions.map((opt) => opt.value));
      return raw
        .split(",")
        .map((value) => value.trim())
        .filter((value): value is VehicleTypeFilterValue =>
          allowed.has(value as VehicleTypeFilterValue)
        );
    },
    serialize: (params: URLSearchParams, value: VehicleTypeFilterValue[]) => {
      if (value && value.length > 0) {
        params.set("vehicletype", value.join(","));
      } else {
        params.delete("vehicletype");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      const allowed = new Set(vehicleTypeOptions.map((opt) => opt.value));
      return raw
        .split(",")
        .every((value) => allowed.has(value.trim() as VehicleTypeFilterValue));
    },
    isActive: (value: VehicleTypeFilterValue[]) =>
      Array.isArray(value) && value.length > 0,
    matches: ({ value, vehicle }) => {
      if (!value || value.length === 0) {
        return true;
      }
      const group = getVehicleTypeGroup(vehicle);
      return Boolean(group && value.includes(group));
    },
    toApi: (value: VehicleTypeFilterValue[]) =>
      value && value.length > 0 ? value : undefined,
    formatChip: (value) => {
      if (!value || value.length === 0) return null;
      return `Fahrzeugtyp: ${value.length} ausgewählt`;
    },
    getOptions: ({ facets }) => {
      const counts = facets?.counts?.vehicletype ?? {};
      return vehicleTypeOptions
        .map((option) => ensureOptionCount(option, counts))
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle) => {
        const group = getVehicleTypeGroup(vehicle);
        return group ? [group] : [];
      },
    },
  } satisfies DefinitionMap["vehicletype"],
  {
    id: "color",
    label: "Farbe",
    param: "color",
    apiKey: "color",
    control: {
      kind: "checkbox",
      multiple: true,
      options: colorOptions,
    } as DefinitionMap["color"]["control"],
    defaultValue: [],
    parse: (params: URLSearchParams) => {
      const raw = params.get("color");
      if (!raw) return [];
      const allowed = new Set(colorOptions.map((opt) => opt.value));
      return raw
        .split(",")
        .map((value) => value.trim())
        .filter((value): value is VehicleColorFilterValue =>
          allowed.has(value as VehicleColorFilterValue)
        );
    },
    serialize: (params: URLSearchParams, value: VehicleColorFilterValue[]) => {
      if (value && value.length > 0) {
        params.set("color", value.join(","));
      } else {
        params.delete("color");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      const allowed = new Set(colorOptions.map((opt) => opt.value));
      return raw
        .split(",")
        .every((value) => allowed.has(value.trim() as VehicleColorFilterValue));
    },
    isActive: (value: VehicleColorFilterValue[]) =>
      Array.isArray(value) && value.length > 0,
    matches: ({ value, vehicle, structure }) => {
      if (!value || value.length === 0) {
        return true;
      }
      const color = resolveVehicleColorValue(vehicle, structure);
      return Boolean(color && value.includes(color));
    },
    toApi: (value: VehicleColorFilterValue[]) =>
      value && value.length > 0 ? value : undefined,
    formatChip: (value, ctx) => {
      if (!value || value.length === 0) return null;
      const labels = value
        .map((id) => ctx.structure?.color?.[id] ?? id)
        .join(", ");
      return labels.length > 0 ? labels : null;
    },
    getOptions: ({ facets, structure }) => {
      const counts = facets?.counts?.color ?? {};
      return colorOptions
        .map((option) => {
          const label = structure.color?.[option.value] ?? option.label;
          return ensureOptionCount({ ...option, label }, counts);
        })
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle, ctx) => {
        const color = resolveVehicleColorValue(vehicle, ctx.structure);
        return color ? [color] : [];
      },
    },
  } satisfies DefinitionMap["color"],
  {
    id: "fuel",
    label: "Kraftstoff",
    param: "fuel",
    apiKey: "fuel",
    control: {
      kind: "checkbox",
      multiple: true,
      options: [], // Will be populated dynamically from structure
    } as DefinitionMap["fuel"]["control"],
    defaultValue: [],
    parse: (params: URLSearchParams) => {
      const raw = params.get("fuel");
      if (!raw) return [];
      return raw.split(",").filter(Boolean);
    },
    serialize: (params: URLSearchParams, value: VehicleFuelFilterValue[]) => {
      if (value && value.length > 0) {
        params.set("fuel", value.join(","));
      } else {
        params.delete("fuel");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      return raw.split(",").every((id) => id.trim().length > 0);
    },
    isActive: (value: VehicleFuelFilterValue[]) => value && value.length > 0,
    matches: ({ value, vehicle }) => {
      if (!value || value.length === 0) return true;
      const fuelId = getVehicleFuelId(vehicle);
      if (!fuelId) return false;
      return value.includes(fuelId);
    },
    toApi: (value: VehicleFuelFilterValue[]) =>
      value && value.length > 0 ? value : undefined,
    formatChip: (value, ctx) => {
      if (!value || value.length === 0) return null;
      const labels = value
        .map((id) => ctx.structure?.fuel?.[id] ?? id)
        .join(", ");
      return labels.length > 0 ? labels : null;
    },
    getOptions: ({ facets, structure }) => {
      const counts = facets?.counts?.fuel ?? {};
      const fuelOptions = Object.entries(structure.fuel || {}).map(
        ([id, label]) => ({
          value: id,
          label: label || id,
        })
      );
      return fuelOptions
        .map((option) => ensureOptionCount(option, counts))
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle) => {
        const fuelId = getVehicleFuelId(vehicle);
        return fuelId ? [fuelId] : [];
      },
    },
  } satisfies DefinitionMap["fuel"],
  {
    id: "transmission",
    label: "Getriebe",
    param: "transmission",
    apiKey: "transmission",
    control: {
      kind: "checkbox",
      multiple: true,
      options: [], // Will be populated dynamically from structure
    } as DefinitionMap["transmission"]["control"],
    defaultValue: [],
    parse: (params: URLSearchParams) => {
      const raw = params.get("transmission");
      if (!raw) return [];
      return raw.split(",").filter(Boolean);
    },
    serialize: (
      params: URLSearchParams,
      value: VehicleTransmissionFilterValue[]
    ) => {
      if (value && value.length > 0) {
        params.set("transmission", value.join(","));
      } else {
        params.delete("transmission");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      return raw.split(",").every((id) => id.trim().length > 0);
    },
    isActive: (value: VehicleTransmissionFilterValue[]) =>
      value && value.length > 0,
    matches: ({ value, vehicle }) => {
      if (!value || value.length === 0) return true;
      const transmissionId = getVehicleTransmissionId(vehicle);
      if (!transmissionId) return false;
      return value.includes(transmissionId);
    },
    toApi: (value: VehicleTransmissionFilterValue[]) =>
      value && value.length > 0 ? value : undefined,
    formatChip: (value, ctx) => {
      if (!value || value.length === 0) return null;
      const labels = value
        .map((id) => ctx.structure?.transmission?.[id] ?? id)
        .join(", ");
      return labels.length > 0 ? labels : null;
    },
    getOptions: ({ facets, structure }) => {
      const counts = facets?.counts?.transmission ?? {};
      const transmissionOptions = Object.entries(
        structure.transmission || {}
      ).map(([id, label]) => ({
        value: id,
        label: label || id,
      }));
      return transmissionOptions
        .map((option) => ensureOptionCount(option, counts))
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle) => {
        const transmissionId = getVehicleTransmissionId(vehicle);
        return transmissionId ? [transmissionId] : [];
      },
    },
  } satisfies DefinitionMap["transmission"],
  {
    id: "location",
    label: "Standort",
    param: "location",
    apiKey: "location",
    control: {
      kind: "checkbox",
      multiple: true,
      options: locationOptions,
    } as DefinitionMap["location"]["control"],
    defaultValue: [],
    parse: (params: URLSearchParams) => {
      const raw = params.get("location");
      if (!raw) return [];
      return raw.split(",").filter(Boolean);
    },
    serialize: (
      params: URLSearchParams,
      value: VehicleLocationFilterValue[]
    ) => {
      if (value && value.length > 0) {
        params.set("location", value.join(","));
      } else {
        params.delete("location");
      }
    },
    validateQuery: (raw: string | null) => {
      if (raw === null) return true;
      return raw.split(",").every((location) => location.trim().length > 0);
    },
    isActive: (value: VehicleLocationFilterValue[]) =>
      value && value.length > 0,
    matches: ({ value, vehicle }) => {
      if (!value || value.length === 0) return true;
      const location = getVehicleLocation(vehicle);
      if (!location) return false;
      return value.includes(location);
    },
    toApi: (value: VehicleLocationFilterValue[]) =>
      value && value.length > 0 ? value : undefined,
    formatChip: (value) => {
      if (!value || value.length === 0) return null;
      return value.join(", ");
    },
    getOptions: ({ facets }) => {
      const counts = facets?.counts?.location ?? {};
      return locationOptions
        .map((option) => ensureOptionCount(option, counts))
        .sort((a, b) => {
          // Sort by count (descending) first, then by label (ascending) as tiebreaker
          const aCount = a.count ?? 0;
          const bCount = b.count ?? 0;
          if (bCount !== aCount) {
            return bCount - aCount;
          }
          return a.label.localeCompare(b.label, "de");
        });
    },
    facet: {
      getKeys: (vehicle) => {
        const location = getVehicleLocation(vehicle);
        return location ? [location] : [];
      },
    },
  } satisfies DefinitionMap["location"],
] as const satisfies ReadonlyArray<VehicleFilterDefinition>;

export { VEHICLE_FILTER_DEFINITIONS };

export type VehicleFilterId = VehicleFilterDefinition["id"];

export type VehicleFilterValues = {
  [Id in VehicleFilterId]: ReturnType<
    Extract<VehicleFilterDefinition, { id: Id }>["parse"]
  >;
};

export type VehicleFilterFacets = {
  total: number;
  counts: Partial<Record<VehicleFilterId, Record<string, number>>>;
};

export type VehicleFilterDefinitionById<Id extends VehicleFilterId> = Extract<
  VehicleFilterDefinition,
  { id: Id }
>;

export type FilterSelectOption<Value extends string> =
  SelectFilterOption<Value>;
